import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { Button, Dropdown } from 'antd';
// eslint-disable-next-line no-use-before-define
import * as React from 'react';

import CreatedByFilter from './CreatedByFilter';
import DateFilter from './DateFilter';
import TagsFilter, { ProjectSearchTag } from './TagsFilter';
import { ProjectSearchUser } from './assignees';
import styles from './filters.module.css';
import { ProjectSearchFilters } from './tokens';

export type ProjectSearchSeverity = {
  id: string;
  label: string;
};

export type ProjectSearchMoreFiltersProps = {
  compact?: boolean;
  currentUser: ProjectSearchUser | null;
  filters: ProjectSearchFilters;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  onChange: (filters: ProjectSearchFilters) => void;
  onOpenChange?: (open: boolean) => void;
  onUserSearchQueryChange?: (query: string) => void;
  open?: boolean;
  severities: ProjectSearchSeverity[];
  showCommentsFilter?: boolean;
  showPublicFeedback?: boolean;
  tags: ProjectSearchTag[];
  users: ProjectSearchUser[];
};

export const PROJECT_SEARCH_SEVERITIES: ProjectSearchSeverity[] = [
  { id: '1', label: 'Critical' },
  { id: '2', label: 'Important' },
  { id: '3', label: 'Normal' },
  { id: '4', label: 'Minor' },
  { id: '0', label: 'Not set' }
];

const toggleToken = (tokens: string[], token: string) =>
  tokens.includes(token)
    ? tokens.filter(value => value !== token)
    : [...tokens, token];

const releasePointerFocus = (event: React.MouseEvent<HTMLButtonElement>) =>
  event.currentTarget.blur();

const MoreFilters: React.FC<ProjectSearchMoreFiltersProps> = ({
  compact = false,
  currentUser,
  filters,
  getPopupContainer,
  onChange,
  onOpenChange,
  onUserSearchQueryChange,
  open,
  severities,
  showCommentsFilter = false,
  showPublicFeedback = true,
  tags,
  users
}) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isOpen = open === undefined ? internalOpen : open;
  const showVisibilityFilter = currentUser?.role !== 'guest';
  const filterCount =
    filters.severity.length +
    filters.tag.length +
    (filters.createdAt ? 1 : 0) +
    filters.createdBy.length +
    (filters.dueDate ? 1 : 0) +
    (showCommentsFilter && filters.comments === 'unread' ? 1 : 0) +
    (filters.visibility ? 1 : 0);

  const handleOpenChange = (nextOpen: boolean) => {
    if (open === undefined) setInternalOpen(nextOpen);
    if (onOpenChange) onOpenChange(nextOpen);
  };

  const secondaryFilters = (
    <div
      className={`${styles.moreSecondaryOptions} ${
        showCommentsFilter ? styles.moreSecondaryOptionsFirst : ''
      }`}
    >
      <TagsFilter
        filters={filters}
        getPopupContainer={getPopupContainer}
        isContainerOpen={isOpen}
        onChange={onChange}
        tags={tags}
      />
      <CreatedByFilter
        currentUser={currentUser}
        filters={filters}
        isContainerOpen={isOpen}
        onChange={onChange}
        onUserSearchQueryChange={onUserSearchQueryChange}
        showPublicFeedback={showPublicFeedback}
        users={users}
      />
      <DateFilter
        isContainerOpen={isOpen}
        onChange={(dueDate, dueDateOperator) =>
          onChange({ ...filters, dueDate, dueDateOperator })
        }
        operator={filters.dueDateOperator}
        sentencePrefix="Due"
        triggerLabel="Due date"
        value={filters.dueDate}
      />
      <DateFilter
        disableFutureDates
        isContainerOpen={isOpen}
        onChange={(createdAt, createdAtOperator) =>
          onChange({ ...filters, createdAt, createdAtOperator })
        }
        operator={filters.createdAtOperator}
        sentencePrefix="Created"
        triggerLabel="Created at"
        value={filters.createdAt}
      />
    </div>
  );

  const visibilityFilters = showVisibilityFilter && (
    <div className={styles.moreGroup}>
      <div className={styles.moreGroupLabel}>Visible to</div>
      <div className={styles.moreOptions}>
        {(
          [
            ['Members only', 'members_only'],
            ['Members & Clients', 'members_and_clients']
          ] as const
        ).map(([label, value]) => (
          <Button
            key={value}
            onClick={() =>
              onChange({
                ...filters,
                visibility: filters.visibility === value ? '' : value
              })
            }
            onMouseUp={releasePointerFocus}
            shape="round"
            size="small"
            type={filters.visibility === value ? 'primary' : 'default'}
          >
            {label}
          </Button>
        ))}
      </div>
    </div>
  );

  const overlay = (
    <div
      className={`${styles.moreDropdownPanel} ${
        compact ? styles.compactMoreDropdownPanel : ''
      }`}
    >
      {showCommentsFilter && secondaryFilters}
      <div className={styles.moreGroup}>
        <div className={styles.moreGroupLabel}>Severity</div>
        <div className={styles.moreOptions}>
          {severities.map(severity => (
            <Button
              key={severity.id}
              onClick={() =>
                onChange({
                  ...filters,
                  severity: toggleToken(filters.severity, severity.id)
                })
              }
              onMouseUp={releasePointerFocus}
              shape="round"
              size="small"
              type={
                filters.severity.includes(severity.id) ? 'primary' : 'default'
              }
            >
              <span
                className={`${styles.severityDot} ${
                  styles[`severityDot${severity.id}`]
                }`}
              />
              {severity.label}
            </Button>
          ))}
        </div>
      </div>
      {visibilityFilters}
      {!showCommentsFilter && secondaryFilters}
      {showCommentsFilter && (
        <div className={`${styles.moreGroup} ${styles.moreCommentsGroup}`}>
          <div className={styles.moreGroupLabel}>Comments</div>
          <div className={styles.moreOptions}>
            <Button
              aria-pressed={!filters.comments}
              className={!filters.comments ? styles.commentsFilterSelected : ''}
              onClick={() => onChange({ ...filters, comments: '' })}
              onMouseUp={releasePointerFocus}
              shape="round"
              size="small"
              type="default"
            >
              <span
                className={`${styles.commentsDot} ${styles.commentsDotAll}`}
              />
              All
            </Button>
            <Button
              aria-pressed={filters.comments === 'unread'}
              className={
                filters.comments === 'unread'
                  ? styles.commentsFilterSelected
                  : ''
              }
              onClick={() => onChange({ ...filters, comments: 'unread' })}
              onMouseUp={releasePointerFocus}
              shape="round"
              size="small"
              type="default"
            >
              <span
                className={`${styles.commentsDot} ${styles.commentsDotUnread}`}
              />
              Unread
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      arrow={compact ? { pointAtCenter: true } : false}
      getPopupContainer={
        getPopupContainer || (trigger => trigger.parentElement || document.body)
      }
      onOpenChange={handleOpenChange}
      open={isOpen}
      overlay={overlay}
      overlayClassName={`${styles.moreDropdownOverlay} ${
        compact ? styles.compactMoreDropdownOverlay : ''
      }`}
      placement={compact ? 'bottomCenter' : 'bottomLeft'}
      trigger={['click']}
    >
      <Button
        className={styles.filterPill}
        shape="round"
        size="small"
        type={isOpen || filterCount ? 'primary' : 'default'}
      >
        {filterCount ? `More (${filterCount})` : 'More'}
        {isOpen ? <UpOutlined /> : <DownOutlined />}
      </Button>
    </Dropdown>
  );
};

export default MoreFilters;
